import usePageSEO from '../hooks/usePageSEO';
import { OrganizationSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import { 
  ArrowRight, 
  ArrowUpRight,
  Target,
  Zap,
  Eye,
  Users,
  MapPin,
  Clock,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

const values = [
  {
    icon: Target,
    title: 'Systems Over Workarounds',
    description: 'We don\'t build band-aid solutions. We architect systems that solve the root complexity of enterprise finance operations.',
  },
  {
    icon: Eye,
    title: 'Absolute Transparency',
    description: 'In finance, trust is everything. Every action our agents take is observable, auditable, and explainable.',
  },
  {
    icon: Zap,
    title: 'Elegance in Complexity',
    description: 'We take the most convoluted workflows in finance and distill them into seamless, intelligent automation.',
  },
];

const jobOpenings = [
  {
    title: 'Senior Full-Stack Engineer',
    location: 'Remote / San Francisco',
    type: 'Full time',
    department: 'Engineering',
  },
  {
    title: 'Senior AI/ML Engineer',
    location: 'Remote / San Francisco',
    type: 'Full time',
    department: 'Engineering',
  },
  {
    title: 'Finance Operations Specialist',
    location: 'Remote',
    type: 'Full time',
    department: 'Operations',
  },
];

export const CompanyPage = () => {
  usePageSEO({
    title: 'About Plouton AI | Advanced Finance Automation Software',
    description: 'Discover how Plouton AI can help your business run smoothly. We build automated finance software that easily scales and requires zero integrations.',
    path: '/company'
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-[108px] overflow-hidden">
      <OrganizationSchema />
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500/8 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-8 leading-tight"
              data-testid="company-headline"
            >
              We are building the future of{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400">
                finance operations.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto mb-10">
              We believe the best finance teams of the future will work alongside AI agents—with more capacity to lead, think strategically, and drive business forward.
            </p>

            <a href="#careers">
              <Button 
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800 px-6 py-5"
              >
                View Open Roles
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-24 relative">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                The Problem
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                The limits of manual operations
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                For decades, brilliant minds in finance have been relegated to the role of data translators: manually bridging the gap between fragmented systems, spreadsheets, and the strategic realities of the business.
              </p>
              <p className="text-slate-400 leading-relaxed">
                Software moved the data around, but humans still had to do all the work—clicking through portals, copying between systems, and reconciling endless transactions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
                <div className="space-y-4">
                  {['Manual data entry across systems', 'Hours spent on reconciliation', 'Fragmented workflows', 'Error-prone processes', 'Limited strategic time'].map((item) => (
                    <div key={item} className="flex items-center gap-3 text-slate-400">
                      <div className="w-2 h-2 rounded-full bg-red-500/60" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-2xl p-8">
                <div className="space-y-4">
                  {['AI agents that work like your best team member', 'Zero integration required', 'Complete visibility and control', 'Regulator-ready audit trails', 'Hours back for strategic work'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
                The Plouton Approach
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Browser agents that actually work
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                We built Plouton to operate directly in your existing systems—no APIs, no integrations, no IT projects. Our agents work exactly like a human would, but faster, more accurate, and around the clock.
              </p>
              <p className="text-slate-400 leading-relaxed">
                By working at the browser level, we can automate workflows that were previously impossible to touch. And because every action is recorded, you have complete visibility into what's happening.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Values
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">
              What drives us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-slate-700 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Meet The Founders
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Born from real pain
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We didn't start Plouton to build another SaaS tool. We started it because we lived the problem ourselves.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Founders Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600/20 via-orange-500/10 to-blue-600/20 rounded-3xl blur-2xl opacity-60" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-800">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/9cmft8xl_1774595723157.jpeg"
                  alt="Plouton AI Founders - Furqan Kidwai CEO and Sarfaraz Iraqui CTO"
                  className="w-full h-auto"
                  width={800}
                  height={450}
                  loading="lazy"
                  data-testid="founders-image"
                />
              </div>
              {/* Backed by badge */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Backed by</div>
                <div className="font-heading font-bold text-slate-900">Antler</div>
              </div>
            </motion.div>

            {/* Founders Story */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-6">
                    From consultancy chaos to AI clarity
                  </h3>
                  <p className="text-slate-400 leading-relaxed mb-4">
                    While running a consultancy with over <span className="text-white font-medium">100+ team members</span>, founders <span className="text-orange-400 font-medium">Sarfraz Shahid Hussain</span> and <span className="text-orange-400 font-medium">Muhammad Furqan Karim Kidwai</span> hit a growth bottleneck that had nothing to do with clients or revenue.
                  </p>
                  <p className="text-slate-400 leading-relaxed mb-4">
                    Complex payroll calculations, final settlement processing for a high-turnover workforce, and month-end close were consuming countless hours. The finance team was drowning in spreadsheets, portals, and manual reconciliation.
                  </p>
                  <p className="text-slate-400 leading-relaxed">
                    After discovering this was a widespread problem across mid-market companies, they founded Plouton AI—building the <span className="text-white font-medium">"virtual employees"</span> they wished they had. AI agents that work directly in your existing tools, requiring zero integrations, with complete audit trails.
                  </p>
                </div>

                {/* Founder names */}
                <div className="pt-6 border-t border-slate-800">
                  <div className="grid grid-cols-2 gap-6">
                    <a 
                      href="https://www.linkedin.com/in/sarfrazhussayn/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <div className="font-heading font-semibold text-white group-hover:text-blue-400 transition-colors flex items-center gap-2">
                        Sarfraz Shahid Hussain
                        <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </div>
                      <div className="text-sm text-slate-500">Co-founder & CTO</div>
                    </a>
                    <a 
                      href="https://www.linkedin.com/in/furqankarimkidwai/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <div className="font-heading font-semibold text-white group-hover:text-blue-400 transition-colors flex items-center gap-2">
                        Muhammad Furqan Karim Kidwai
                        <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </div>
                      <div className="text-sm text-slate-500">Co-founder & CEO</div>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-24" id="careers">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Careers
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Join the team
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We're a team of engineers, finance veterans, and builders creating the future of finance operations. If you're excited about AI and automation, we want to meet you.
            </p>
          </motion.div>

          <div className="space-y-4">
            {jobOpenings.map((job, index) => (
              <motion.a
                key={job.title}
                href={`mailto:careers@plouton.co?subject=Application: ${job.title}`}
                className="block bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-slate-700 hover:bg-slate-900/80 transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors flex items-center gap-2">
                      {job.title}
                      <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {job.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {job.department}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="text-slate-500 mb-4">
              Don't see the right role? We're always looking for exceptional people.
            </p>
            <a href="mailto:careers@plouton.co">
              <Button 
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Send us your resume
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Ready to see Plouton in action?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Book a demo to see how we can automate your finance workflows.
          </p>
          <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
            <Button 
              className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-5 font-semibold"
              data-testid="company-book-demo"
            >
              Book a Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompanyPage;
